import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CutsList from './CutsList';
import PrimalCutsGraphic from './PrimalCutsGraphic';
import CutsHeader from './CutsHeader';
import CutsPromos from './CutsPromos';
import CutsResultsHeader from './CutsResultsHeader';
import { labelForSelection } from './primalRegions';
import { AJAX_URL } from '../../config/index.js';

export default function Cuts({ pageId }) {
  const [primalCuts, setPrimalCuts] = useState([]);
  const [cookingMethods, setCookingMethods] = useState([]);
  const [cutsList, setCutsList] = useState([]);
  const [promos, setPromos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cookingMethodFilter, setCookingMethodFilter] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [leanOnly, setLeanOnly] = useState(false);
  const [favoriteError, setFavoriteError] = useState('');
  const [pendingFavorites, setPendingFavorites] = useState([]);
  const pendingFavoritesRef = useRef(new Set());
  const urlAppliedRef = useRef(false);

  // Favorites require an authenticated WordPress user.
  const [isLoggedIn] = useState(
    () =>
      typeof document !== 'undefined' &&
      document.body.classList.contains('logged-in')
  );

  // Load everything once. All filtering (primal, cooking method, search) runs
  // client-side against this list, so results are instant — no reload/skeleton.
  useEffect(() => {
    let isSubscribed = true;
    const url = `${AJAX_URL}post_wlQueryCuts`;
    const payload = new FormData();
    if (pageId) payload.append('pageId', pageId);

    axios
      .post(url, payload)
      .then((res) => {
        if (!isSubscribed) return;
        setPrimalCuts(res.data.data.primalCuts);
        setCookingMethods(res.data.data.cookingMethods);
        setCutsList(res.data.data.cuts);
        setPromos(res.data.data.promos || []);
        setLoaded(true);
      })
      .catch((err) => console.log(err.message));

    return () => (isSubscribed = false);
  }, []);

  // Apply URL params once the primal list has loaded: ?primal={slug} pre-selects
  // a primal (e.g. from a cut detail breadcrumb); ?search={term} pre-fills search.
  useEffect(() => {
    if (urlAppliedRef.current || primalCuts.length === 0) return;
    urlAppliedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const primalSlug = params.get('primal');
    const searchParam = params.get('search');

    if (searchParam) setSearchTerm(searchParam);

    if (primalSlug && !searchParam) {
      setPrimalCuts((prev) =>
        prev.map((item) => ({ ...item, selected: item.slug === primalSlug }))
      );

      setTimeout(() => {
        const results = document.getElementById('cuts-results');
        if (results) results.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [primalCuts]);

  const selectedPrimals = primalCuts.filter((item) => item.selected);
  const selectedPrimalIds = selectedPrimals.map((item) => Number(item.id));

  const trimmedSearch = searchTerm.trim().toLowerCase();
  const displayedCuts = cutsList.filter((cut) => {
    const matchesPrimal =
      selectedPrimalIds.length === 0 ||
      selectedPrimalIds.includes(Number(cut.primal_cut));
    const matchesSearch =
      !trimmedSearch || cut.name.toLowerCase().includes(trimmedSearch);
    const matchesCookingMethod =
      !cookingMethodFilter ||
      (Array.isArray(cut.cooking_methods) &&
        cut.cooking_methods.map(Number).includes(Number(cookingMethodFilter)));
    const matchesFavorite = !favoritesOnly || !!cut.is_favorite;
    return (
      matchesPrimal && matchesSearch && matchesCookingMethod && matchesFavorite &&
      (!leanOnly || !!cut.lean)
    );
  });

  // A region can select more than one primal (loin ⇢ Loin + Sirloin). Build the
  // results-header primal from the selection: the alias label + the display
  // primal's description/butcher's note.
  const headingLabel = labelForSelection(selectedPrimals);
  const displayPrimal =
    selectedPrimals.find(
      (item) => item.name.toLowerCase() === (headingLabel || '').toLowerCase()
    ) ||
    selectedPrimals[0] ||
    null;
  const resultsPrimal = selectedPrimals.length
    ? {
        name: headingLabel,
        description: displayPrimal ? displayPrimal.description : '',
        butchers_note: displayPrimal ? displayPrimal.butchers_note : ''
      }
    : null;

  // Search highlights are visual only; they never become primal filters.
  const highlightedPrimalIds = trimmedSearch
    ? [...new Set(displayedCuts.map((cut) => Number(cut.primal_cut)))]
    : [];

  const clearSearchParams = () => {
    const url = new URL(window.location.href);
    ['search', 's', 'primal'].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  };

  const updateSearch = (value) => {
    if (!value.trim()) clearSearchParams();
    setSearchTerm(value);
    setPrimalCuts((prev) => prev.map((item) => ({ ...item, selected: false })));
  };

  const selectPrimals = (ids) => {
    clearSearchParams();
    setSearchTerm('');
    setPrimalCuts((prev) =>
      prev.map((item) => ({ ...item, selected: ids.includes(item.id) }))
    );
  };

  const updateFavorite = (id, favorite) => {
    setCutsList((prev) => prev.map((cut) =>
      cut.id === id ? { ...cut, is_favorite: favorite } : cut
    ));
  };

  const toggleFavorite = (cut) => {
    if (pendingFavoritesRef.current.has(cut.id)) return;
    if (!isLoggedIn) {
      window.location.href = '/login?redirect_to=' + encodeURIComponent(window.location.href);
      return;
    }
    const previous = !!cut.is_favorite;
    pendingFavoritesRef.current.add(cut.id);
    setPendingFavorites([...pendingFavoritesRef.current]);
    setFavoriteError('');
    updateFavorite(cut.id, !previous);

    const payload = new FormData();
    payload.append('id', cut.id);
    axios.post(`${AJAX_URL}post_wl_handle_favorites`, payload)
      .then(({ data }) => {
        if (!data.success) throw new Error('Unable to save cut');
        updateFavorite(cut.id, data.data.action === 'added');
      })
      .catch(() => {
        updateFavorite(cut.id, previous);
        setFavoriteError('Your saved cuts could not be updated. Please try again.');
      })
      .then(() => {
        pendingFavoritesRef.current.delete(cut.id);
        setPendingFavorites([...pendingFavoritesRef.current]);
      });
  };

  const clearFilters = () => {
    clearSearchParams();
    setPrimalCuts((prev) => prev.map((item) => ({ ...item, selected: false })));
    setSearchTerm('');
    setCookingMethodFilter('');
    setFavoritesOnly(false);
    setLeanOnly(false);
  };

  return (
    <>
      <CutsHeader searchTerm={searchTerm} setSearchTerm={updateSearch}>
        <PrimalCutsGraphic
          onSelect={selectPrimals}
          primalCuts={primalCuts}
          highlightedPrimalIds={highlightedPrimalIds}
        />
      </CutsHeader>

      {loaded && (
        <div id="cuts-results">
          <CutsResultsHeader
            selectedPrimal={resultsPrimal}
            searchTerm={searchTerm}
            favoritesOnly={favoritesOnly}
            setFavoritesOnly={setFavoritesOnly}
            isLoggedIn={isLoggedIn}
            leanOnly={leanOnly}
            setLeanOnly={setLeanOnly}
            resultCount={displayedCuts.length}
            cookingMethods={cookingMethods}
            cookingMethodFilter={cookingMethodFilter}
            setCookingMethodFilter={setCookingMethodFilter}
            onClear={clearFilters}
          />
          {favoriteError && (
            <p className="container" role="alert">{favoriteError}</p>
          )}
          {displayedCuts.length > 0 ? (
            <CutsList
              cutsList={displayedCuts}
              onToggleFavorite={toggleFavorite}
              pendingFavorites={pendingFavorites}
            />
          ) : (
            <div style={{ textAlign: 'center', marginBottom: '50px' }}>
              No results found.
            </div>
          )}
        </div>
      )}

      {/* Promos follow the results. */}
      <CutsPromos promos={promos} />
    </>
  );
}
